import { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import { isEqual } from 'lodash';
import Yup from '../../utils/yup';

import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import useMenus from '../../context/MenuContext';
import useUsers from '../../context/UsersContext';
import { FormButtonPanelUI, FormHeaderUI, FormBase, DeleteDialogUI } from '../Interface';
import { cleanValues, generateAutocompleteItems } from '../../utils/helper';
import useConfig from '../../context/ConfigContext';
import masterDataService from '../../service/masterData.service';
import { useLoader } from '../../provider/LoaderProvider';
import dayjs from 'dayjs';
import BudgetFields from './BudgetFields';
import BudgetTabs from './BudgetTabs';
import BudgetReports from './BudgetReports';
import budgetService from '../../service/budget.service';
import useParam from '../../context/ParamContext';
import propertyService from '../../service/property.service';

export const BudgetPage = () => {
  const { loading, showLoader, hideLoader } = useLoader();
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const { getUser, user } = useUsers();
  const { getConfig, config } = useConfig();
  const { params, getParam } = useParam();
  const [refetchTable, setRefetchTable] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [canEdit, setCanEdit] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [defaultValues, setDefaultValues] = useState({});
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [initValues, setInitValues] = useState({});
  const [defaultSelectedValues, setDefaultSelectedValues] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [autoCompleteOptions, setAutoCompleteOptions] = useState({
    projects: [],
    currency: [],
    items: [],
  });
  const [autocompleteData, setAutoCompleteData] = useState({});
  const [projectSubmenuId, setProjectSubmenuId] = useState(null);
  const [dynProps, setDynProps] = useState([]);
  const [dynLoading, setDynLoading] = useState(true);

  const defaultBudgetLine = {
    id: null,
    isNew: true,
    itemCode: '',
    itemName: '',
    name: '',
    kbt: '',
    qty: '',
    unitPrice: null,
    sumPrice: null,
    unit: '',
  };

  const def = {
    projectId: null,
    date: dayjs(),
    currencyCode: '',
    budgetLine: [],
  };

  useEffect(() => {
    getMenus();
    if (!Object.keys(user).length) getUser();
    if (!Object.keys(config).length) getConfig();
    if (!Object.keys(params).length) getParam();

    setDefaultValues(def);
    setDefaultSelectedValues(def);
  }, []);

  useEffect(() => {
    if (Object.keys(params).length > 0) initDefaultValues();
  }, [params]);

  useEffect(() => {
    if (projectSubmenuId && Object.keys(params.length > 0)) getFormProperties();
  }, [projectSubmenuId, params]);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('budget');
    setCanEdit(getCanEdit(submenuId));

    const projectSubmenuId = getMenuSubMenuId('Project');
    setProjectSubmenuId(projectSubmenuId);
  }, [menus]);

  useEffect(() => {
    if (!loading && Object.keys(config).length > 0) {
      setDefaultValues((prev) => {
        return {
          ...prev,
          currencyCode: config?.currencyCode ?? null,
        };
      });
    }
  }, [config, loading]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultSelectedValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultValues);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultValues);
    }
  }, [selectedValues]);

  const getFormProperties = () => {
    const propertyIds = params.BUDGET_SHOW_DYN_PROPS_BY_PROPERTYID
      ? JSON.parse(params.BUDGET_SHOW_DYN_PROPS_BY_PROPERTYID)
      : [];
    if (propertyIds.length === 0) {
      setDynLoading(false);
      return;
    }

    propertyService.getPropertiesByFormId(projectSubmenuId).then((data) => {
      const filteredData = data.filter((item) => propertyIds.includes(item.propertyId));
      setDynProps(filteredData);
      setDynLoading(false);
    });
  };

  const validationSchema = Yup.object().shape({
    projectId: Yup.string().required(),
    date: Yup.string().required(),
    currencyCode: Yup.string().required(),
    budgetLine: Yup.array().of(
      Yup.object().shape({
        itemCode: Yup.string().required(),
        itemName: Yup.string().required(),
        name: Yup.string().required(),
        kbt: Yup.string().required(),
        qty: Yup.string().required(),
        unitPrice: Yup.string().required(),
        sumPrice: Yup.string().required(),
        unit: Yup.string().required(),
      })
    ),
  });

  const initDefaultValues = () => {
    showLoader();
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .currencyToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    if (params.BUDGET_ITEMS_BY_ITEMGROUP_ID) {
      promiseArray.push(
        new Promise((resolve, reject) => {
          masterDataService
            .itemsByGroupId(params.BUDGET_ITEMS_BY_ITEMGROUP_ID)
            .then((data) => {
              resolve(data);
            })
            .catch((e) => {
              reject(e);
            });
        })
      );
    } else {
      promiseArray.push(
        new Promise((resolve, reject) => {
          masterDataService
            .itemsFromTableToForm()
            .then((data) => {
              resolve(data);
            })
            .catch((e) => {
              reject(e);
            });
        })
      );
    }

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          projects: resp[0],
          currency: resp[1],
          items: resp[2],
        };

        setAutoCompleteData(aData);

        const filteredProject = aData.projects.filter((p) => p.active);

        const acOptions = {
          projects: generateAutocompleteItems(filteredProject, ['projectName', 'orderNumber'], 'projectId'),
          currency: generateAutocompleteItems(aData.currency, 'code', 'code'),
          items: generateAutocompleteItems(aData.items, 'itemCode', 'itemCode'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const valuesToForm = () => {
    const { id, projectId, date, currencyCode, budgetLine } = selectedValues;

    setInitValues({
      id,
      projectId,
      date: dayjs(date),
      currencyCode,
      companyName: selectedValues.project?.company?.name,
      projectOrderNumber: selectedValues.project?.orderNumber,
      budgetLine: budgetLine?.map((bl) => ({
        id: bl.id,
        itemCode: bl.itemCode,
        itemName: bl.itemName,
        name: bl.name,
        kbt: bl.kbt,
        qty: bl.qty,
        unitPrice: bl.unitPrice,
        sumPrice: bl.sumPrice,
        createdBy: bl.createdBy,
        unit: bl.unit,
      })),
    });
  };

  const handleSubmit = (values, { resetForm }) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      handleInsertObject(values, resetForm);
    }
  };

  const updateObject = async (values) => {
    const updData = {
      ...cleanValues(values),
      date: dayjs(values.date).startOf('day').add(12, 'hour').toISOString(),
    };

    budgetService.updateItem(updData).then((data) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });

      setSelectedValues(data);
      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);
    });
  };

  const handleInsertObject = async (values) => {
    const insertObject = {
      ...cleanValues(values),
      date: dayjs(values.date).startOf('day').add(12, 'hour').toISOString(),
    };

    budgetService
      .createItem(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });

        setSelectedValues(data);
        setRowSelectionModel(data.id);
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
      })
      .finally(() => {
        //@TODO loader
      });
  };

  const handleRemoveElement = async () => {
    budgetService.deleteItem(selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues({});
      setInitValues(def);
    });
  };

  return (
    <>
      <FormBase
        name="Budget"
        leftPanelContent={
          <>
            <div className="h-full overflow-auto custom-scrollbar">
              <FormHeaderUI
                addClick={() => {
                  setSelectedValues(defaultSelectedValues);
                  setViewField(false);
                  setAddField(false);
                }}
                addDisabled={!viewField || !canEdit}
                modClick={() => {
                  setViewField(false);
                  setEditButtonvisible(false);
                }}
                deleteClick={() => {
                  setShowDeleteDialog(true);
                }}
                deleteDisabled={!viewField || operatorsDisabled || !canEdit}
                modDisabled={!viewField || operatorsDisabled || !canEdit}
                copyClick={() => {
                  setViewField(false);
                  setAddField(false);
                }}
                copyDisabled={!viewField || operatorsDisabled || !canEdit}
              />

              <Formik
                initialValues={initValues}
                validationSchema={validationSchema}
                validateOnChange={false}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ resetForm }) => (
                  <Form>
                    <BudgetFields
                      viewField={viewField}
                      autoCompleteOptions={autoCompleteOptions}
                      selectedValues={selectedValues}
                      autocompleteData={autocompleteData}
                      dynProps={dynProps}
                    />

                    <BudgetTabs
                      disabled={viewField}
                      defaultBudgetLine={defaultBudgetLine}
                      autoCompleteOptions={autoCompleteOptions}
                      autocompleteData={autocompleteData}
                    />

                    <FormButtonPanelUI
                      onCancelClick={() => {
                        setViewField(true);
                        setEditButtonvisible(true);
                        setAddField(true);

                        resetForm();
                      }}
                      disabled={editButtonVisible && addField}
                    />
                  </Form>
                )}
              </Formik>
            </div>
          </>
        }
        rightPanelContent={
          !dynLoading && (
            <BudgetReports
              refetch={{ refetchTable, setRefetchTable }}
              values={{ selectedValues, setSelectedValues }}
              viewField={viewField}
              operatorsDisabled={operatorsDisabled}
              rowSelection={{ rowSelectionModel, setRowSelectionModel }}
              dynProps={dynProps}
            />
          )
        }
      />
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};
